import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {memo, useMemo} from 'react'
import type {Block} from '@/_new-code/services/kontent-ai/types'
import type {BazaarVoiceRatingsContentItem} from '@/_new-code/products/ratings'
import {BazaarVoiceRatingsBlock} from '@/_new-code/products/ratings'
import type {SkuProviderContentItem} from '@/_new-code/products/sku-provider'
import {SkuProviderBlock} from '@/_new-code/products/sku-provider'
import type {PearCommerceWidgetContentItem} from '@/_new-code/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import {PearCommerceWidget} from '@/_new-code/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import type {PDPHeaderAccordionContentItem} from '@/_new-code/products/pdp/pdp-accordion/pdp-accordion'
import {PDPHeaderAccordionBlock} from '@/_new-code/products/pdp/pdp-accordion/pdp-accordion'
import type {PDPPopupRebatesContentItem} from '@/_new-code/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import {PDPPopupRebatesBlock} from '@/_new-code/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import type {PDPPopupWithRichTextContentItem} from '@/_new-code/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import {PDPPopupWithRichTextBlock} from '@/_new-code/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import type {PDPSliderContentItem} from '@/_new-code/products/flexible-web-toolkit/blocks/slider'
import {SliderBlock} from '@/_new-code/products/flexible-web-toolkit/blocks/slider'
import {TextButton} from '../../flexible-web-toolkit/components/button/text-button'
import {
	PDPFindAVetCard,
	type PDPFindAVetCardContentItem,
} from '../pdp-find-a-vet-card/pdp-find-a-vet-card'
import {getBrandColor} from '../../flexible-web-toolkit/blocks/helpers'
import {getBrandColorClasses} from '../utils/brand-colors'

export type PDPHeaderBlockContentItem = IContentItem<{
	slider: Elements.LinkedItemsElement<PDPSliderContentItem>
	title: Elements.TextElement
	productLabelText: Elements.TextElement
	productLabelUrl: Elements.TextElement
	safetyDataText: Elements.TextElement
	safetyDataUrl: Elements.TextElement
	description: Elements.TextElement
	ratings: Elements.LinkedItemsElement<BazaarVoiceRatingsContentItem>
	skuProvider: Elements.LinkedItemsElement<SkuProviderContentItem>
	pearCommerce: Elements.LinkedItemsElement<PearCommerceWidgetContentItem>
	pdpHeaderAccordion: Elements.LinkedItemsElement<PDPHeaderAccordionContentItem>
	pdpPopupRebates: Elements.LinkedItemsElement<PDPPopupRebatesContentItem>
	pdpPopupRichText: Elements.LinkedItemsElement<PDPPopupWithRichTextContentItem>
	pdpWithFindAVetCard: Elements.LinkedItemsElement<PDPFindAVetCardContentItem>
}>

const PDPHeaderBlockComponent: Block<PDPHeaderBlockContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	// Destructure elements with explicit types for clarity
	const {
		slider,
		title,
		productLabelText,
		productLabelUrl,
		safetyDataText,
		safetyDataUrl,
		description,
		ratings,
		skuProvider,
		pearCommerce,
		pdpHeaderAccordion,
		pdpPopupRebates,
		pdpPopupRichText,
		pdpWithFindAVetCard,
	} = block.elements

	const {brandColorClasses} = useMemo(() => {
		const pageConfig = page.elements.config[0]
		const color = pageConfig ? getBrandColor(pageConfig) : null
		const classes = getBrandColorClasses(color)
		return {brandColor: color, brandColorClasses: classes}
	}, [page.elements.config])

	return (
		<div
			className="pdp-header-container mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-12"
			data-kontent-element-codename="pdp_header_block"
		>
			{/* Desktop Layout: Slider on left, content on right */}
			<div className="flex flex-col gap-6 lg:flex-row lg:gap-12 xl:gap-16">
				{/* Left Column - Sticky Slider */}
				{/* lg:!top-20 = 80px offset to account for fixed header height */}
				<div className="order-1 hidden w-full lg:sticky lg:!top-20 lg:block lg:h-fit lg:w-1/2 lg:!pt-8">
					{/* Slider for desktop */}
					{slider.length > 0 && slider[0] ? (
						<div data-kontent-element-codename="slider">
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'left',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}
				</div>

				{/* Right Column */}
				<div className="order-2 flex-1 space-y-6 lg:order-2">
					{/* Title */}
					{title ? (
						<h1
							className="text-gray-900 text-2xl font-bold lg:text-3xl"
							data-kontent-element-codename="title"
						>
							{title}
						</h1>
					) : null}

					{/* Product Buttons */}
					{(productLabelText && productLabelUrl) ||
					(safetyDataText && safetyDataUrl) ? (
						<div
							className="flex flex-col gap-3 sm:flex-row sm:gap-4"
							data-kontent-element-codename="product_label_text"
						>
							<div className="flex flex-wrap gap-3">
								{productLabelText && productLabelUrl ? (
									<TextButton
										className="!text-sm font-light not-italic"
										href={productLabelUrl}
										target="_blank"
										variant="none"
									>
										{productLabelText}
									</TextButton>
								) : null}
								{safetyDataUrl && safetyDataText ? (
									<TextButton
										className="ml-2 !text-sm font-light not-italic"
										href={safetyDataUrl}
										target="_blank"
										variant="none"
									>
										{safetyDataText}
									</TextButton>
								) : null}
							</div>
						</div>
					) : null}

					{/* Description */}
					{description ? (
						<div
							className="text-gray-900 text-base leading-relaxed lg:text-base"
							data-kontent-element-codename="description"
						>
							{description}
						</div>
					) : null}

					{/* Ratings*/}
					{ratings.length > 0 && ratings[0] ? (
						<div data-kontent-element-codename="ratings">
							<BazaarVoiceRatingsBlock
								BlockMapper={BlockMapper}
								block={ratings[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{/* PDP Popup Components - Rebate Available & Prescription Required buttons */}
					{pdpPopupRebates.length > 0 ||
					pdpPopupRichText.length > 0 ? (
						<div className="flex flex-wrap items-center gap-3">
							{pdpPopupRebates.length > 0 &&
							pdpPopupRebates[0] ? (
								<div data-kontent-element-codename="pdp_popup_rebates">
									<PDPPopupRebatesBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRebates[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
							{pdpPopupRichText.length > 0 &&
							pdpPopupRichText[0] ? (
								<div data-kontent-element-codename="pdp_popup_rich_text">
									<PDPPopupWithRichTextBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRichText[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
						</div>
					) : null}

					{/* Slider (MOBILE ONLY) */}
					{slider.length > 0 && slider[0] ? (
						<div
							className="lg:hidden"
							data-kontent-element-codename="slider"
						>
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'left',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{/* SKU Provider */}
					{skuProvider.length > 0 && skuProvider[0] ? (
						<div data-kontent-element-codename="sku_provider">
							<SkuProviderBlock
								BlockMapper={BlockMapper}
								block={skuProvider[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					<div className="flex flex-col gap-[20px]">
						<div>
							{pdpWithFindAVetCard.length > 0 &&
							pdpWithFindAVetCard[0] ? (
								<p className="text-[16px] font-black md:font-medium">
									{
										pdpWithFindAVetCard[0].elements
											.buyOrFindAVetCardTitle
									}
								</p>
							) : null}
						</div>
						<div className="flex flex-col gap-[16px] md:flex-row md:gap-[10px]">
							{pdpWithFindAVetCard.length > 0 &&
							pdpWithFindAVetCard[0] ? (
								<div
									className={`mx-auto max-w-[328px] basis-4/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
									data-kontent-element-codename="pdp_with_find_a_vet_card"
								>
									<PDPFindAVetCard
										BlockMapper={BlockMapper}
										block={pdpWithFindAVetCard[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
							{/* Pear Commerce Widget */}
							{pearCommerce.length > 0 && pearCommerce[0] ? (
								<div
									className={`mx-auto max-w-[362px] basis-5/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
									data-kontent-element-codename="pear_commerce"
								>
									<PearCommerceWidget
										BlockMapper={BlockMapper}
										block={pearCommerce[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
						</div>
					</div>

					{/* PDP Header Accordion */}
					{pdpHeaderAccordion.length > 0 ? (
						<div
							className="w-full"
							data-kontent-element-codename="pdp_header_accordion"
						>
							{pdpHeaderAccordion.map((accordion) => (
								<PDPHeaderAccordionBlock
									BlockMapper={BlockMapper}
									block={accordion}
									globalConfig={globalConfig}
									key={accordion.system.id}
									page={page}
								/>
							))}
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}

export const PDPHeaderBlock = memo(PDPHeaderBlockComponent)
